package org.teddapp.project.ui.notes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Note
import com.tedd.db.Notebook
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddMenu
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.projects.EntityColorPicker
import org.teddapp.project.ui.projects.EntityIconPicker
import org.teddapp.project.ui.projects.parseHexCrud
import org.teddapp.project.ui.skills.parseTags
import org.teddapp.project.ui.teddOverlayShadow

/** Une couleur stockée en « #RRGGBB », ou null si elle est absente ou illisible. */
internal fun noteColor(hex: String?): Color? =
    hex?.takeIf { it.isNotBlank() }?.let { runCatching { Color(parseHexCrud(it)) }.getOrNull() }

/** La pastille de couleur d'une note ou d'un carnet. */
@Composable
internal fun ColorDot(color: Color, size: Int = 8) {
    Box(Modifier.size(size.dp).background(color, CircleShape))
}

/**
 * Les étiquettes d'une note, sous son titre : une puce par étiquette, une croix pour
 * l'ôter, et un champ pour en ajouter. Le champ propose les étiquettes déjà connues :
 * une faute de frappe ferait sinon deux étiquettes pour une même chose.
 */
@Composable
internal fun NoteTags(note: Note, model: NotesModel) {
    var typed by remember(note.id) { mutableStateOf("") }
    fun commit(raw: String) {
        if (raw.isNotBlank()) model.addTag(note, raw)
        typed = ""
    }
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            parseTags(note.tags).forEach { tag ->
                TagPill(tag, onRemove = { model.removeTag(note, tag) })
            }
            BasicTextField(
                value = typed,
                onValueChange = { v ->
                    // Une virgule valide l'étiquette, comme dans un champ d'adresses.
                    if (v.contains(',')) commit(v.substringBefore(',')) else typed = v
                },
                singleLine = true,
                textStyle = TeddType.Caption.copy(color = TeddTokens.Ink),
                cursorBrush = SolidColor(TeddTokens.Ink),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                keyboardActions = KeyboardActions(onDone = { commit(typed) }),
                modifier = Modifier
                    .widthIn(min = 120.dp)
                    .height(26.dp)
                    .onPreviewKeyEvent { e ->
                        if (e.type == KeyEventType.KeyDown && e.key == Key.Enter) { commit(typed); true } else false
                    },
                decorationBox = { inner ->
                    Box(Modifier.padding(horizontal = 4.dp), contentAlignment = Alignment.CenterStart) {
                        if (typed.isEmpty()) Text("+ étiquette", style = TeddType.Caption, color = TeddTokens.InkSubtle)
                        inner()
                    }
                },
            )
        }
        val suggestions = model.suggestTags(note, typed)
        if (suggestions.isNotEmpty()) {
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                suggestions.forEach { s ->
                    Text(
                        text = "#$s",
                        style = TeddType.Caption,
                        color = TeddTokens.Link,
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .clickable { commit(s) }
                            .padding(horizontal = 6.dp, vertical = 3.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun TagPill(tag: String, onRemove: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = Modifier.height(26.dp).background(TeddTokens.SurfaceSunken, shape).padding(start = 8.dp, end = 2.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text("#$tag", style = TeddType.Caption, color = TeddTokens.InkMuted)
        TeddIconButton(onClick = onRemove, size = 22.dp) {
            TeddIcon(name = "x", size = 11.dp, color = TeddTokens.InkSubtle)
        }
    }
}

/** La couleur de la note : un bouton qui la montre, et la palette en menu. */
@Composable
internal fun NoteColorButton(note: Note, model: NotesModel) {
    var open by remember { mutableStateOf(false) }
    val current = noteColor(note.color)
    Box {
        TeddIconButton(onClick = { open = true }, active = open) {
            Box(
                Modifier
                    .size(14.dp)
                    .then(
                        if (current != null) Modifier.background(current, CircleShape)
                        else Modifier.border(1.5.dp, TeddTokens.InkSubtle, CircleShape),
                    ),
            )
        }
        TeddMenu(
            expanded = open,
            onDismiss = { open = false },
            width = 200.dp,
            alignEnd = true,
            entries = listOf(
                TeddMenuEntry("Sans couleur", onClick = { model.setColor(note, null) }, icon = "x", selected = current == null),
            ) + NotesModel.NoteColors.zip(listOf("Rouge", "Ambre", "Vert", "Bleu", "Indigo", "Rose")).map { (hex, name) ->
                TeddMenuEntry(
                    name,
                    onClick = { model.setColor(note, hex) },
                    leading = { noteColor(hex)?.let { ColorDot(it, size = 12) } },
                    selected = note.color.equals(hex, ignoreCase = true),
                )
            },
        )
    }
}

/** Importer un fichier Markdown en note. */
@Composable
internal fun NotesImportButton(model: NotesModel) {
    TeddIconButton(onClick = model::importMarkdown) {
        TeddIcon(name = "arrow-down", size = 16.dp, color = TeddTokens.InkMuted)
    }
}

/** L'ordre de la liste. */
@Composable
internal fun NotesSortButton(model: NotesModel) {
    var open by remember { mutableStateOf(false) }
    Box {
        TeddIconButton(onClick = { open = true }, active = open) {
            TeddIcon(name = "sort", size = 16.dp, color = TeddTokens.InkMuted)
        }
        TeddMenu(
            expanded = open,
            onDismiss = { open = false },
            width = 230.dp,
            alignEnd = true,
            entries = NotesSort.entries.map { s ->
                TeddMenuEntry(s.label, onClick = { model.sort = s }, selected = model.sort == s)
            },
        )
    }
}

/**
 * Créer ou modifier un carnet : son nom, son icône, sa couleur. En modification, on
 * peut aussi y créer un sous-dossier ou le supprimer.
 */
@Composable
internal fun BookDialog(
    title: String,
    book: Notebook?,
    onConfirm: (name: String, icon: String, color: String) -> Unit,
    onDelete: (() -> Unit)? = null,
    onSub: (() -> Unit)? = null,
    onClose: () -> Unit,
) {
    var name by remember(book) { mutableStateOf(book?.name.orEmpty()) }
    var icon by remember(book) { mutableStateOf(book?.icon ?: NotesModel.BookIcons.first()) }
    var color by remember(book) { mutableStateOf(book?.color ?: NotesModel.NoteColors[3]) }
    androidx.compose.ui.window.Dialog(
        onDismissRequest = onClose,
        properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Column(
            modifier = Modifier
                .padding(TeddSpace.S4)
                .widthIn(max = 420.dp)
                .teddOverlayShadow(RoundedCornerShape(TeddTokens.RLg))
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                .padding(TeddSpace.S6),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
        ) {
            Text(title, style = TeddType.Title2, color = TeddTokens.Ink)
            BookNameField(name) { name = it }
            EntityIconPicker(palette = NotesModel.BookIcons, value = icon, color = color, onChange = { icon = it })
            EntityColorPicker(value = color, onChange = { color = it })
            if (onSub != null) {
                TeddButton(text = "Créer un sous-dossier ici", onClick = onSub, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
            }
            if (onDelete != null) {
                Text(
                    text = "Supprimer ce dossier ne supprime rien : ses notes reviennent dans " +
                        "« Toutes », ses sous-dossiers remontent à la racine.",
                    color = TeddTokens.InkMuted,
                    fontSize = 12.sp,
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(
                    text = "Enregistrer",
                    onClick = { onConfirm(name, icon, color) },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    modifier = Modifier.weight(1f),
                )
                if (onDelete != null) {
                    TeddButton(text = "Supprimer", onClick = onDelete, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
                }
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
            }
        }
    }
}

@Composable
private fun BookNameField(value: String, onChange: (String) -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    BasicTextField(
        value = value,
        onValueChange = onChange,
        singleLine = true,
        textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
        cursorBrush = SolidColor(TeddTokens.Ink),
        modifier = Modifier
            .width(372.dp)
            .height(40.dp)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.LineStrong, shape),
        decorationBox = { inner ->
            Box(Modifier.padding(horizontal = TeddSpace.S3), contentAlignment = Alignment.CenterStart) {
                if (value.isEmpty()) Text("Nom du carnet", color = TeddTokens.InkSubtle, style = TeddType.Body)
                inner()
            }
        },
    )
}
